import { useEffect, useState, type ReactNode } from 'react';
import { cn } from '@/lib/cn';

/**
 * Recharts defaults in the A3 brand palette (ARCHITECTURE §11 "Charts").
 *
 *   const c = useChartTheme();
 *   <LineChart data={d}>
 *     <CartesianGrid {...c.grid} />
 *     <XAxis dataKey="day" {...c.xAxis} />
 *     <YAxis {...c.yAxis} />
 *     <Tooltip content={<ChartTooltip />} cursor={c.cursor} />
 *     <Line dataKey="sent" stroke={c.series[0]} {...c.line} />
 *   </LineChart>
 *
 * Single-series bars are brand blue with the current period in saffron and its value labelled:
 *   <Bar dataKey="visits" {...c.bar}>
 *     {d.map((row, i) => <Cell key={i} fill={c.barFill(i === d.length - 1)} />)}
 *   </Bar>
 * Past/comparison series use `c.muted`. Label or legend every series beyond the fourth.
 *
 * Colours are resolved from CSS variables at runtime so charts follow light/dark mode.
 */
const VARS = [
  '--gh-chart-1',
  '--gh-chart-2',
  '--gh-chart-3',
  '--gh-chart-4',
  '--gh-chart-5',
  '--gh-chart-6',
  '--gh-chart-current',
  '--gh-chart-muted',
  '--gh-chart-grid',
  '--gh-chart-axis',
  '--gh-ink',
  '--gh-surface',
  '--gh-hairline',
] as const;
const FALLBACK = [
  '#1F5A8E',
  '#E3A23B',
  '#2B9590',
  '#B5532A',
  '#6B95C4',
  '#4A5563',
  '#E3A23B',
  '#8494A6',
  '#E1E7EE',
  '#56677A',
  '#0E1C2B',
  '#FFFFFF',
  '#E1E7EE',
];

function readVars() {
  if (typeof document === 'undefined') return FALLBACK;
  const cs = getComputedStyle(document.documentElement);
  return VARS.map((v, i) => cs.getPropertyValue(v).trim() || FALLBACK[i]!);
}

export function useChartTheme() {
  const [vals, setVals] = useState(readVars);
  useEffect(() => {
    const obs = new MutationObserver(() => setVals(readVars()));
    obs.observe(document.documentElement, { attributes: true, attributeFilter: ['data-theme'] });
    return () => obs.disconnect();
  }, []);
  const [c1, c2, c3, c4, c5, c6, current, muted, grid, axis, ink, surface, hairline] =
    vals as string[];
  const tick = { fill: axis, fontSize: 11, fontFamily: 'var(--gh-font-sans)' };
  return {
    series: [c1!, c2!, c3!, c4!, c5!, c6!],
    /** Saffron: the current period / the one bar that matters. */
    current: current!,
    /** Past or comparison series (3:1 on the surface). */
    muted: muted!,
    /** Fill for a single-series bar: brand blue, saffron when it is the current period. */
    barFill: (isCurrent: boolean) => (isCurrent ? current! : c1!),
    /** Value label for the current bar (saffron-ink text reads on white; saffron does not). */
    currentLabel: {
      fill: 'var(--gh-accent-ink)',
      fontSize: 12,
      fontWeight: 800,
      fontFamily: 'var(--gh-font-sans)',
    },
    ink: ink!,
    surface: surface!,
    grid: { stroke: grid, vertical: false },
    xAxis: { tick, tickLine: false, axisLine: { stroke: hairline }, tickMargin: 8, minTickGap: 16 },
    yAxis: { tick, tickLine: false, axisLine: false, width: 44, tickMargin: 4 },
    cursor: { stroke: axis, strokeWidth: 1, strokeDasharray: '3 3' },
    barCursor: { fill: grid, opacity: 0.5 },
    /** Linear, never monotone: daily whole counts must not grow invented shoulders or peaks. */
    line: {
      type: 'linear' as const,
      strokeWidth: 2,
      dot: false,
      activeDot: { r: 4, strokeWidth: 2, stroke: surface },
    },
    area: { type: 'linear' as const, strokeWidth: 2, fillOpacity: 0.08 },
    bar: { radius: [4, 4, 0, 0] as [number, number, number, number], maxBarSize: 36 },
  };
}

interface TooltipPayloadItem {
  name?: ReactNode;
  value?: number | string;
  color?: string;
  dataKey?: string | number;
}

/** Tooltip content for recharts: <Tooltip content={<ChartTooltip format={formatNumber} />} /> */
export function ChartTooltip({
  active,
  payload,
  label,
  format = (v) => String(v),
  labelFormat,
  className,
}: {
  active?: boolean;
  payload?: TooltipPayloadItem[];
  label?: ReactNode;
  format?: (v: number | string) => ReactNode;
  labelFormat?: (l: ReactNode) => ReactNode;
  className?: string;
}) {
  if (!active || !payload?.length) return null;
  return (
    <div
      className={cn(
        'min-w-36 rounded-md border border-hairline bg-surface-raised px-3 py-2.5 text-sm shadow-md',
        className,
      )}
    >
      {label !== undefined && (
        <div className="eyebrow mb-1.5">{labelFormat ? labelFormat(label) : label}</div>
      )}
      <div className="grid gap-1">
        {payload.map((p, i) => (
          <div key={i} className="flex items-center justify-between gap-4">
            <span className="flex items-center gap-1.5 text-ink-muted">
              <span className="size-2 rounded-[2px]" style={{ background: p.color }} />
              {p.name}
            </span>
            <span className="num font-extrabold text-ink">
              {p.value !== undefined ? format(p.value) : '–'}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}

/** Simple legend row matching the tooltip swatches. */
export function ChartLegend({
  items,
  className,
}: {
  items: { label: ReactNode; color: string }[];
  className?: string;
}) {
  return (
    <div className={cn('flex flex-wrap gap-x-4 gap-y-1 text-sm text-ink-muted', className)}>
      {items.map((it, i) => (
        <span key={i} className="inline-flex items-center gap-1.5">
          <span className="size-2.5 rounded-[2px]" style={{ background: it.color }} />
          {it.label}
        </span>
      ))}
    </div>
  );
}
